import React, { useState } from "react";
import { ChevronDown, ChevronUp, Shield, RefreshCw, HelpCircle, Building2, TrendingUp, Users, FileText } from "lucide-react";

// TaxBuddy logo — blue, distinct from BudgetBuddy green
function TaxBuddyLogo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="tb-about-bg" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1d4ed8"/>
          <stop offset="100%" stopColor="#1e1b4b"/>
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="13" fill="url(#tb-about-bg)"/>
      <rect x="13" y="8" width="17" height="22" rx="2.5" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.75"/>
      <line x1="17" y1="14" x2="26" y2="14" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="17" y1="18" x2="26" y2="18" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="17" y1="22" x2="23" y2="22" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="33" cy="33" r="9" fill="#1d4ed8" stroke="#fff" strokeWidth="1.5"/>
      <text x="33" y="37" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800" fontFamily="system-ui">$</text>
    </svg>
  );
}

const SUPPORTED_BANKS = [
  "ING Orange Everyday","Commonwealth Bank","Westpac","ANZ","NAB",
  "Macquarie","Bendigo Bank","Suncorp","St George","BankWest",
  "Bank of Melbourne","Up Bank","UBank","ME Bank","Heritage Bank",
  "Beyond Bank","Teachers Mutual","Firefighters Mutual","Health Professionals Bank",
  "HSBC Australia","AMP Bank","MyState Bank","Police Bank","RACQ Bank",
  "P&N Bank","Australian Military Bank","Sydney Credit Union","Railways Credit Union",
];

const FAQS = [
  { q:"Is my data safe?", a:"Yes. Your transaction data is stored securely and never sold to anyone. You can delete your account and all data permanently at any time from Settings." },
  { q:"Do you store my bank statements?", a:"We store your transaction data (dates, amounts, descriptions, categories) so you can review and export them. We do not store the original PDF files after processing." },
  { q:"What if my bank isn't supported?", a:"Email findjoy143@gmail.com with your bank name. We will add support within 24–48 hours." },
  { q:"Is TaxBuddy suitable for small businesses?", a:"Yes — TaxBuddy works for individuals, sole traders, and small business owners. It categorises expenses into ATO-aligned categories that work for both personal and small business returns. For complex accounting, use it alongside your registered tax agent." },
  { q:"Can I use TaxBuddy for investment income?", a:"Yes. TaxBuddy recognises dividend income, interest, rental deposits, and investment property expenses. It flags these automatically so nothing gets missed." },
  { q:"How accurate is the categorisation?", a:"Categorisation is automated using ATO guidelines and is a guide only. We recommend reviewing your transactions before exporting. One click to change any category." },
  { q:"Can I upload multiple years of statements?", a:"Yes. Upload as many statements as you like across multiple years. TaxBuddy detects duplicates automatically — no double-ups." },
  { q:"How long does processing take?", a:"A full year of statements (10–20 files) typically takes under a minute. Please keep the page open while it works." },
  { q:"What's the best way to upload?", a:"Drop all your statements for the financial year in one go. TaxBuddy splits large batches automatically and skips any duplicates." },
  { q:"Can my accountant use the export?", a:"Yes — the Excel export is designed to hand straight to your accountant. It includes a full transaction list, deductions summary, income summary, and a needs-review sheet." },
  { q:"Do I need an accountant to use TaxBuddy?", a:"No. You can use it to prepare your own return or to give your accountant a clean summary — potentially saving on accounting fees." },
  { q:"What does Pro give me?", a:"Free plan: upload and review up to 1 statement. Pro: unlimited statements and the Excel export to give your accountant. See Pricing for details." },
  { q:"Can I get a refund?", a:"Annual plans (Pro Annual at $79/year) come with a 14-day automatic refund. If you request a refund within 14 days of purchase it is processed automatically — no questions asked. Monthly plans can be cancelled anytime and access continues to the end of the billing period. Australian Consumer Law guarantees apply to all purchases. Contact findjoy143@gmail.com if you have a concern." },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left gap-4">
        <span className="text-sm font-semibold text-gray-900">{q}</span>
        {open
          ? <ChevronUp size={15} className="text-gray-400 flex-shrink-0" />
          : <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-sm text-gray-600 pb-4 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function About() {
  return (
    <div className="max-w-xl mx-auto py-8 px-4 space-y-10" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Logo + Hero */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <TaxBuddyLogo size={64} />
        </div>
        <h1 className="text-3xl font-black text-gray-900 leading-tight tracking-tight">
          Tax time shouldn't<br/>cost you sleep.
        </h1>
        <p className="text-gray-500 text-sm leading-relaxed">
          TaxBuddy was built by a working mum — Venus — who was burnt out, time-poor,
          and drowning in bank statements every June. She needed something simple:
          a tool that reads her statements, sorts her transactions, and produces something
          clean to hand to her accountant. Nothing like that existed. So she built it.
        </p>
      </div>

      {/* About FindJoy143 */}
      <div className="rounded-2xl p-6 space-y-3" style={{ backgroundColor: "#eff6ff" }}>
        <div className="flex items-center gap-2 mb-1">
          <TaxBuddyLogo size={28} />
          <h2 className="text-base font-bold text-gray-900">About FindJoy143</h2>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          TaxBuddy is part of <strong>FindJoy143</strong> — a small family of digital tools
          built by one Australian founder with a full-time job and a simple goal: build
          something useful enough to one day make the 9-to-5 optional.
        </p>
        <p className="text-sm text-gray-700 leading-relaxed">
          <strong>"143"</strong> is Filipino for <em>I love you</em> — 1 letter, 4 letters, 3 letters.
          It's a quiet shortcut for something big. In Filipino culture it's widely known,
          used the way others might send a heart emoji. FindJoy143 is a reminder to find
          joy in the everyday, even when life is full and hard and the to-do list never ends.
        </p>
        <p className="text-sm text-gray-500">
          Contact: <a href="mailto:findjoy143@gmail.com" className="text-blue-700 underline font-medium">findjoy143@gmail.com</a>
        </p>
      </div>

      {/* Who it's for */}
      <div className="space-y-3">
        <h2 className="text-xl font-black text-gray-900 tracking-tight">Who TaxBuddy is for</h2>
        {[
          { icon: Users, title: "Busy individuals", desc: "Anyone who doesn't have time to manually sort a year of bank statements before tax time." },
          { icon: Building2, title: "Sole traders & small businesses", desc: "Separate business from personal spending, track deductible expenses, and hand your accountant a clean summary." },
          { icon: TrendingUp, title: "Investors & landlords", desc: "Automatically identifies dividends, interest, rental income, and investment property expenses across all your accounts." },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="flex gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <Icon size={17} className="text-blue-700" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className="space-y-3">
        <h2 className="text-xl font-black text-gray-900 tracking-tight">How it works</h2>
        {[
          { n:"1", t:"Upload your statements", d:"Drop your PDF bank statements from any supported Australian bank — upload a full year at once." },
          { n:"2", t:"TaxBuddy reads and sorts them", d:"Transactions are extracted and categorised using ATO-aligned categories. Transfers and duplicates are filtered automatically." },
          { n:"3", t:"You review and confirm", d:"Check, correct, and add notes to any transaction. One click to change a category. You make the final call on everything." },
          { n:"4", t:"Export for your accountant", d:"Download a clean Excel workbook ready to hand straight to your accountant — or use it to prepare your own return." },
        ].map(({ n, t, d }) => (
          <div key={n} className="flex gap-3">
            <div className="w-7 h-7 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
              style={{ background: "linear-gradient(135deg, #1d4ed8, #1e1b4b)" }}>{n}</div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{t}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{d}</p>
            </div>
          </div>
        ))}
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mt-2">
          <p className="text-xs text-blue-700"><strong>Tip:</strong> Upload all statements for the full financial year in one batch. Keep the page open while it processes — large batches take 1–2 minutes.</p>
        </div>
      </div>

      {/* How we categorise */}
      <div className="space-y-2">
        <h2 className="text-xl font-black text-gray-900 tracking-tight">How we categorise</h2>
        <p className="text-sm text-gray-600 leading-relaxed">All categories follow ATO guidelines for individuals, sole traders, and investors. Deductible expenses are flagged automatically. Transfers between your own accounts are excluded. Categorisation is a guide — always confirm with your tax agent before lodging.</p>
      </div>

      {/* Supported banks */}
      <div className="space-y-3">
        <h2 className="text-xl font-black text-gray-900 tracking-tight">Supported banks</h2>
        <p className="text-xs text-gray-500">Don't see yours? Email us — we'll add it within 24–48 hours.</p>
        <div className="grid grid-cols-2 gap-2">
          {SUPPORTED_BANKS.map(bank => (
            <div key={bank} className="text-xs bg-gray-50 border border-gray-100 text-gray-700 px-3 py-2 rounded-lg">
              {bank}
            </div>
          ))}
        </div>
        <a href="mailto:findjoy143@gmail.com?subject=Add my bank&body=Hi, my bank isn't listed. Bank name: "
          className="inline-block text-xs text-blue-700 underline font-semibold">
          My bank isn't listed — email us to add it
        </a>
      </div>

      {/* Legal */}
      <div className="space-y-5 border-t border-gray-100 pt-8">
        <div className="flex gap-3">
          <Shield size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-gray-900 text-sm mb-1">Disclaimer</p>
            <p className="text-xs text-gray-500 leading-relaxed">TaxBuddy is a tax categorisation tool only. It does not provide financial, tax, or legal advice. Always consult a registered tax agent before lodging. FindJoy143 accepts no liability for errors in categorisation or decisions made based on TaxBuddy data.</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Shield size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-gray-900 text-sm mb-1">Privacy</p>
            <p className="text-xs text-gray-500 leading-relaxed">Your data is stored securely and used only to provide the TaxBuddy service. We never sell or share your data. Delete your account and all data at any time from Settings. We comply with the Australian Privacy Act 1988.</p>
          </div>
        </div>
        <div className="flex gap-3">
          <RefreshCw size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-gray-900 text-sm mb-1">Refunds</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              <strong className="text-gray-700">Annual plans (Pro Annual — $79/year):</strong> 14-day automatic refund. Request within 14 days of purchase and your refund is processed immediately — no questions asked. After 14 days, Pro access continues to the end of your annual period.<br/><br/>
              <strong className="text-gray-700">Monthly plans (Pro Monthly — $9/month):</strong> Cancel anytime. Pro access continues to the end of your billing period. No refund for the current month.<br/><br/>
              Australian Consumer Law guarantees apply to all purchases. Contact <a href="mailto:findjoy143@gmail.com" className="underline text-blue-700">findjoy143@gmail.com</a> if you have any concerns.
            </p>
          </div>
        </div>
        <div className="text-center pt-2">
          <p className="text-xs text-gray-400">🇦🇺 Australian-owned and built for Australians</p>
          <p className="text-xs text-gray-400 mt-1">FindJoy143 · findjoy143@gmail.com · © 2025</p>
        </div>
      </div>

      {/* FAQ */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle size={18} className="text-blue-700" />
          <h2 className="text-xl font-black text-gray-900 tracking-tight">Frequently asked questions</h2>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 px-5">
          {FAQS.map(faq => <FAQItem key={faq.q} q={faq.q} a={faq.a} />)}
        </div>
      </div>

    </div>
  );
}
